import {
  AnalyticsPermissions,
  ImsPermissions,
  SettingsPermissions,
} from "@redotech/account-common/permissions";
import { isRedoEmailAddress } from "@redotech/account-common/redo-domains";
import { useRequiredContext } from "@redotech/react-util/context";
import type { PageContext } from "@redotech/redo-agents-common/page-context";
// redont-allow-dynamic-import: route-level code splitting for carved analytics package
import { AmplitudeSessionReplayProvider } from "@redotech/redo-merchant-analytics-app/analytics/amplitude-session-replay-provider";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { useCanAccessCatalog } from "@redotech/redo-merchant-app-common/catalog/catalog-access";
import { GlobalAgentContext } from "@redotech/redo-merchant-app-common/global-agent-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import {
  PermissionedRouteObject,
  PermissionHandle,
} from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { teamCanCycleCount } from "@redotech/redo-merchant-inventory-app/cycle-counts/cycle-counts-available";
import { teamHasRefulfillmentEnabled } from "@redotech/redo-return-settings-primitives-common/returns-center-config";
import { breadcrumbSlot } from "@redotech/redo-web/breadcrumb";
import { memo, ReactNode, useContext, useEffect } from "react";
import { matchPath, Navigate, useLocation } from "react-router-dom";
import { defaultChildRoute } from "./legacy-redirects";
import type { RoutePageComponent } from "./session-replay-route-page";

/**
 * Permission-aware redirect for the IMS index route.
 * Redirects to the first page the user has both feature-flag access and
 * permission access to, falling back to onboarding.
 */
const ImsIndexRedirect = memo(function ImsIndexRedirect() {
  const team = useRequiredContext(TeamContext);
  const routes = useRequiredContext(MerchantRoutesContext);
  const canOpenCatalogList = useCanAccessCatalog();
  const imsEnabled = team.settings.ims?.enabled ?? false;
  const forecastingEnabled = team.settings.ims?.forecastingEnabled ?? false;
  const forecastingBillingEnabled =
    team.settings.ims?.forecastingBilling?.enabled ?? false;

  const canAccessInventory = useHasPermission(ImsPermissions.ACCESS_INVENTORY);
  const canAccessCycleCounts = useHasPermission(
    ImsPermissions.ACCESS_CYCLE_COUNTS,
  );
  const canAccessForecasting = useHasPermission(
    ImsPermissions.ACCESS_FORECASTING,
  );
  const canAccessSuppliers = useHasPermission(ImsPermissions.ACCESS_SUPPLIERS);
  const canAccessPurchaseOrders = useHasPermission(
    ImsPermissions.ACCESS_PURCHASE_ORDERS,
  );
  const canAccessShipments = useHasPermission(ImsPermissions.ACCESS_SHIPMENTS);
  const canAccessTransfers = useHasPermission(ImsPermissions.ACCESS_TRANSFERS);
  const canAccessReporting = useHasPermission(ImsPermissions.VIEW_REPORTS);

  // Redirect to the first page the user can access
  if (imsEnabled && canAccessInventory) {
    return <Navigate replace to="products/all" />;
  }
  if (canOpenCatalogList) {
    return <Navigate replace to={routes.catalogProductsHref} />;
  }
  if (imsEnabled && canAccessCycleCounts && teamCanCycleCount(team)) {
    return <Navigate replace to="intelligent-cycle-counts" />;
  }
  if (
    (forecastingEnabled || forecastingBillingEnabled) &&
    canAccessForecasting
  ) {
    return <Navigate replace to="forecasting/monthly" />;
  }
  if (imsEnabled && canAccessSuppliers) {
    return <Navigate replace to="suppliers" />;
  }
  if (imsEnabled && canAccessPurchaseOrders) {
    return <Navigate replace to="purchase-orders/all" />;
  }
  if (imsEnabled && canAccessShipments) {
    return <Navigate replace to="shipments/all" />;
  }
  if (imsEnabled && canAccessTransfers) {
    return <Navigate replace to="transfers" />;
  }
  if (imsEnabled && canAccessReporting) {
    return <Navigate replace to="reports" />;
  }
  return <Navigate replace to="onboarding" />;
});

const InventoryAnalyticsIndexRedirect = memo(
  function InventoryAnalyticsIndexRedirect() {
    const team = useRequiredContext(TeamContext);
    const canAccessCycleCounts = useHasPermission(
      ImsPermissions.ACCESS_CYCLE_COUNTS,
    );

    return (
      <Navigate
        replace
        to={
          canAccessCycleCounts && teamCanCycleCount(team)
            ? "cycle-counts"
            : "reports"
        }
      />
    );
  },
);

const cycleCountsPermission: PermissionHandle = ({ team, userPermissions }) =>
  !!userPermissions?.has(ImsPermissions.ACCESS_CYCLE_COUNTS) &&
  teamCanCycleCount(team);

const supplierDetailsBreadcrumb = breadcrumbSlot();
const purchaseOrderBreadcrumb = breadcrumbSlot();
const purchaseOrderViewBreadcrumb = breadcrumbSlot();
const shipmentViewBreadcrumb = breadcrumbSlot();
const shipmentDetailBreadcrumb = breadcrumbSlot();
const inventoryLevelsViewBreadcrumb = breadcrumbSlot();

const HIDE_PADDING_ROUTES: string[] = [
  "/forecasting/monthly",
  "/forecasting/recommendations",
  "/forecasting/rules",
  "/adjustments",
  "/analytics/cycle-counts",
  "/analytics/cost-of-goods-sold",
  "/analytics/inventory-valuation",
  "/intelligent-cycle-counts",
  "/intelligent-cycle-counts/:viewName",
  "/intelligent-cycle-counts/:viewName/:countId",
  "/intelligent-cycle-counts/rules",
  "/intelligent-cycle-counts/rules/new",
  "/intelligent-cycle-counts/rules/:ruleId",
  "/products",
  "/products/:viewName",
  "/adjustment-report",
  "/liquidation/products",
  "/suppliers",
  "/suppliers/:supplierId",
  "/purchase-orders",
  "/purchase-orders/:purchaseOrderId",
  "/shipments",
  "/shipments/damaged",
  "/shipments/:viewName",
  "/shipments/:viewName/:shipmentId",
  "/scenario-planning",
  "/scenario-planning/:scenarioId",
  "/analytics/managed-returns",
  "/analytics/reports",
  "/analytics/reports/*",
  "/consignment",
  "/transfers",
  "/transfers/new",
  "/transfers/:transferId",
  "/document-templates",
  "/document-templates/:templateId",
  "/barcode-label-templates",
  "/barcode-label-templates/:templateId",
  "/intelligent-inventory",
  "/intelligent-inventory/:threadId",
];

const INVENTORY_ROUTE_BASE = "/stores/:storeId/inventory";

export function isCycleCountsRoute(pathname: string): boolean {
  return (
    !!matchPath(
      `${INVENTORY_ROUTE_BASE}/intelligent-cycle-counts/*`,
      pathname,
    ) || !!matchPath(`${INVENTORY_ROUTE_BASE}/analytics/cycle-counts`, pathname)
  );
}

export function isForecastingRoute(pathname: string): boolean {
  return !!matchPath(`${INVENTORY_ROUTE_BASE}/forecasting/*`, pathname);
}

function hidePadding(url: string): boolean {
  return HIDE_PADDING_ROUTES.some(
    (route) => !!matchPath(`${INVENTORY_ROUTE_BASE}${route}`, url),
  );
}

const FULLSCREEN_ROUTES: string[] = ["/document-templates/:templateId"];

const HIDE_HEADER_ROUTES: string[] = [
  ...FULLSCREEN_ROUTES,
  "/forecasting/recommendations",
  "/forecasting/rules",
];
function hideHeader(url: string): boolean {
  return HIDE_HEADER_ROUTES.some(
    (route) => !!matchPath(`${INVENTORY_ROUTE_BASE}${route}`, url),
  );
}

const HIDE_NAV_ROUTES: string[] = [...FULLSCREEN_ROUTES];

function hideNav(url: string): boolean {
  return HIDE_NAV_ROUTES.some(
    (route) => !!matchPath(`${INVENTORY_ROUTE_BASE}${route}`, url),
  );
}

const INVENTORY_ASK_REDO_CONTEXT = {
  pageTitle: "Inventory",
  pageDescription:
    "The merchant is using Inventory Management. Use the inventory skill for questions about catalog products, inventory levels, suppliers, bills of materials, purchase orders, transfers, forecasting, reporting, and inventory workflows.",
  requiredSkill: "inventory",
  suggestedQuestions: [
    "Which inventory actions can Ask Redo help with?",
    "How do I update stock levels?",
    "How should I manage suppliers and purchase orders?",
  ],
} satisfies PageContext;

const InventoryAskRedoRouteContext = memo(
  function InventoryAskRedoRouteContext({ children }: { children: ReactNode }) {
    const { setPageContext } = useContext(GlobalAgentContext);

    useEffect(() => {
      setPageContext(INVENTORY_ASK_REDO_CONTEXT);
      return () => setPageContext(undefined);
    }, [setPageContext]);

    return <>{children}</>;
  },
);

const InventoryRoutesShell = memo(function InventoryRoutesShell({
  Page,
}: {
  Page: RoutePageComponent;
}) {
  const location = useLocation();

  return (
    <AmplitudeSessionReplayProvider
      enabled={isCycleCountsRoute(location.pathname)}
      productId="cycle-counts"
    >
      <AmplitudeSessionReplayProvider
        enabled={isForecastingRoute(location.pathname)}
        productId="forecasting"
      >
        <Page
          hideHeader={hideHeader}
          hideNavbar={hideNav}
          hidePadding={hidePadding}
        />
      </AmplitudeSessionReplayProvider>
    </AmplitudeSessionReplayProvider>
  );
});

export const inventoryRoutes: PermissionedRouteObject[] = [
  {
    handle: { breadcrumb: "Inventory" },
    async lazy() {
      const { Page } = await import("../../page");
      const { ImsStatusWrapper } =
        await import("@redotech/redo-merchant-inventory-app/components/ims-status-wrapper");
      return {
        element: (
          <ImsStatusWrapper>
            <InventoryAskRedoRouteContext>
              <InventoryRoutesShell Page={Page} />
            </InventoryAskRedoRouteContext>
          </ImsStatusWrapper>
        ),
      };
    },
    children: [
      { index: true, element: <ImsIndexRedirect /> },
      {
        path: "onboarding",
        handle: { breadcrumb: "Get started" },
        async lazy() {
          const { ImsOnboardingSplash } =
            await import("@redotech/redo-merchant-inventory-app/onboarding/ims-onboarding-splash");
          return { Component: ImsOnboardingSplash };
        },
      },
      {
        path: "products",
        handle: {
          breadcrumb: "Inventory levels",
          permission: ImsPermissions.ACCESS_INVENTORY,
        },
        children: [
          defaultChildRoute("all"),
          {
            path: ":viewName",
            handle: { breadcrumb: inventoryLevelsViewBreadcrumb },
            async lazy() {
              const { InventoryProductsWrapper } =
                await import("@redotech/redo-merchant-inventory-app/inventory-products/inventory-products");
              const { CatalogProductModalHost } =
                await import("@redotech/redo-merchant-catalog-app/catalog/catalog-product-modal");
              return {
                element: (
                  <>
                    <InventoryProductsWrapper
                      breadcrumb={inventoryLevelsViewBreadcrumb}
                    />
                    <CatalogProductModalHost />
                  </>
                ),
              };
            },
            children: [
              {
                index: true,
                async lazy() {
                  const { InventoryProductsPage } =
                    await import("@redotech/redo-merchant-inventory-app/inventory-products/inventory-products");
                  return { Component: InventoryProductsPage };
                },
              },
            ],
          },
        ],
      },
      {
        path: "adjustment-report",
        handle: {
          breadcrumb: "Adjustment reports",
          permission: ImsPermissions.ACCESS_INVENTORY,
        },
        async lazy() {
          const { AdjustmentReportPage } =
            await import("@redotech/redo-merchant-inventory-app/inventory-products/adjustment-report");
          return { Component: AdjustmentReportPage };
        },
      },
      {
        path: "forecasting",
        handle: {
          breadcrumb: "Forecasting",
          permission: ImsPermissions.ACCESS_FORECASTING,
        },
        children: [
          { index: true, element: <Navigate replace to="monthly" /> },
          {
            path: "monthly",
            handle: { breadcrumb: "Monthly forecast" },
            async lazy() {
              const { ForecastingAllPage } =
                await import("@redotech/redo-merchant-inventory-app/forecasting/forecasting-all-page");
              return { Component: ForecastingAllPage };
            },
          },
          {
            path: "recommendations",
            handle: { breadcrumb: "Purchasing recommendations" },
            async lazy() {
              const { PurchaseOrderRecommendationsPage } =
                await import("@redotech/redo-merchant-inventory-app/forecasting/purchase-order-recommendations-page");
              const { CatalogProductModalHost } =
                // redont-allow-dynamic-import: route-level code splitting for the product modal
                await import("@redotech/redo-merchant-catalog-app/catalog/catalog-product-modal");
              return {
                element: (
                  <>
                    <PurchaseOrderRecommendationsPage />
                    <CatalogProductModalHost />
                  </>
                ),
              };
            },
          },
          {
            path: "rules",
            handle: { breadcrumb: "Rules" },
            async lazy() {
              const { PurchaseOrderRecommendationRulesPage } =
                await import("@redotech/redo-merchant-inventory-app/forecasting/purchase-order-recommendation-rules-page");
              return { Component: PurchaseOrderRecommendationRulesPage };
            },
          },
        ],
      },
      {
        path: "adjustments",
        handle: {
          breadcrumb: "Inventory adjustments",
          permission: ImsPermissions.ACCESS_INVENTORY,
        },
        async lazy() {
          const { InventoryAdjustments } =
            await import("@redotech/redo-merchant-inventory-app/adjustments/inventory-adjustments");
          return { Component: InventoryAdjustments };
        },
      },
      {
        path: "intelligent-cycle-counts",
        handle: {
          breadcrumb: "Cycle counts",
          permission: cycleCountsPermission,
        },
        children: [
          defaultChildRoute("all"),
          {
            path: ":viewName",
            async lazy() {
              const { IntelligentCycleCountsWrapper } =
                await import("@redotech/redo-merchant-inventory-app/cycle-counts/intelligent-cycle-counts-page");
              return { element: <IntelligentCycleCountsWrapper /> };
            },
            children: [
              {
                index: true,
                async lazy() {
                  const { IntelligentCycleCountsPage } =
                    await import("@redotech/redo-merchant-inventory-app/cycle-counts/intelligent-cycle-counts-page");
                  return { Component: IntelligentCycleCountsPage };
                },
              },
              {
                path: ":countId",
                handle: { breadcrumb: "Detail" },
                async lazy() {
                  const { IntelligentCycleCountDetailPage } =
                    await import("@redotech/redo-merchant-inventory-app/cycle-counts/intelligent-cycle-count-detail");
                  return { element: <IntelligentCycleCountDetailPage /> };
                },
              },
            ],
          },
          {
            path: "rules",
            handle: {
              breadcrumb: "Rules",
              permission: ImsPermissions.MANAGE_CYCLE_COUNT_RULES,
            },
            children: [
              {
                index: true,
                async lazy() {
                  const { RulesPage } =
                    await import("@redotech/redo-merchant-inventory-app/cycle-counts/rules-page");
                  return { element: <RulesPage /> };
                },
              },
              {
                path: "new",
                handle: { breadcrumb: "New rule" },
                async lazy() {
                  const { RuleEditPage } =
                    await import("@redotech/redo-merchant-inventory-app/cycle-counts/rule-edit-page");
                  return { element: <RuleEditPage /> };
                },
              },
              {
                path: ":ruleId",
                handle: { breadcrumb: "Edit rule" },
                async lazy() {
                  const { RuleEditPage } =
                    await import("@redotech/redo-merchant-inventory-app/cycle-counts/rule-edit-page");
                  return { element: <RuleEditPage /> };
                },
              },
            ],
          },
        ],
      },
      {
        path: "reports",
        handle: {
          breadcrumb: "Reports",
          permission: ImsPermissions.VIEW_REPORTS,
        },
        async lazy() {
          const { InventoryReporting } =
            await import("@redotech/redo-merchant-inventory-app/reporting/inventory-reporting");
          return { Component: InventoryReporting };
        },
      },
      {
        path: "analytics",
        handle: { breadcrumb: "Analytics" },
        children: [
          { index: true, element: <InventoryAnalyticsIndexRedirect /> },
          {
            path: "cycle-counts",
            handle: {
              breadcrumb: "Cycle counts",
              permission: cycleCountsPermission,
            },
            async lazy() {
              // redont-allow-dynamic-import: route-level code splitting for the inventory analytics page
              const { CycleCountsAnalyticsPage } =
                await import("@redotech/redo-merchant-inventory-app/analytics/cycle-counts-analytics-page");
              return { Component: CycleCountsAnalyticsPage };
            },
          },
          {
            path: "cost-of-goods-sold",
            handle: {
              breadcrumb: "Cost of goods sold",
              permission: ImsPermissions.ACCESS_INVENTORY,
            },
            async lazy() {
              const { CostOfGoodsSoldPage } =
                // redont-allow-dynamic-import: route-level code splitting for the inventory analytics page
                await import("@redotech/redo-merchant-inventory-app/analytics/cost-of-goods-sold-page");
              const { CatalogProductModalHost } =
                // redont-allow-dynamic-import: route-level code splitting for the product modal
                await import("@redotech/redo-merchant-catalog-app/catalog/catalog-product-modal");
              return {
                element: (
                  <>
                    <CostOfGoodsSoldPage />
                    <CatalogProductModalHost />
                  </>
                ),
              };
            },
          },
          {
            path: "inventory-valuation",
            handle: {
              breadcrumb: "Inventory valuation",
              permission: ImsPermissions.ACCESS_INVENTORY,
            },
            async lazy() {
              const { InventoryValuationPage } =
                // redont-allow-dynamic-import: route-level code splitting for the inventory analytics page
                await import("@redotech/redo-merchant-inventory-app/analytics/inventory-valuation-page");
              const { CatalogProductModalHost } =
                // redont-allow-dynamic-import: route-level code splitting for the product modal
                await import("@redotech/redo-merchant-catalog-app/catalog/catalog-product-modal");
              return {
                element: (
                  <>
                    <InventoryValuationPage />
                    <CatalogProductModalHost />
                  </>
                ),
              };
            },
          },
          {
            path: "reports",
            handle: {
              breadcrumb: "Custom analytics",
              permission: ImsPermissions.VIEW_REPORTS,
            },
            children: [
              {
                index: true,
                handle: { permission: AnalyticsPermissions.VIEW_ANALYTICS },
                async lazy() {
                  const { InventoryCustomReportsTab } =
                    // redont-allow-dynamic-import: route-level code splitting for carved analytics package
                    await import("@redotech/redo-merchant-analytics-app/analytics/reports/custom/inventory-custom-reports");
                  return { Component: InventoryCustomReportsTab };
                },
              },
              {
                path: "custom/:customReportId",
                handle: {
                  hideHeader: true,
                  permission: AnalyticsPermissions.VIEW_ANALYTICS,
                },
                async lazy() {
                  const { InventoryCustomReportPage } =
                    // redont-allow-dynamic-import: route-level code splitting for carved analytics package
                    await import("@redotech/redo-merchant-analytics-app/analytics/reports/custom/inventory-custom-reports");
                  return { Component: InventoryCustomReportPage };
                },
              },
            ],
          },
        ],
      },
      {
        path: "intelligent-inventory",
        handle: {
          breadcrumb: "Intelligent inventory",
          permission: ImsPermissions.ACCESS_INTELLIGENT_INVENTORY,
        },
        children: [
          {
            index: true,
            async lazy() {
              const { IntelligentInventoryPage } =
                await import("@redotech/redo-merchant-inventory-app/intelligent-inventory/intelligent-inventory-page");
              return { Component: IntelligentInventoryPage };
            },
          },
          {
            path: ":threadId",
            async lazy() {
              const { IntelligentInventoryPage } =
                await import("@redotech/redo-merchant-inventory-app/intelligent-inventory/intelligent-inventory-page");
              return { Component: IntelligentInventoryPage };
            },
          },
        ],
      },
      {
        path: "scenario-planning",
        handle: {
          breadcrumb: "Scenario planning",
          permission: ImsPermissions.ACCESS_SCENARIO_PLANNING,
        },
        children: [
          {
            index: true,
            Component: () => (
              <RedoEmployeeAccessGuard>
                <Navigate replace to={`new-${Date.now()}`} />
              </RedoEmployeeAccessGuard>
            ),
          },
          {
            path: ":scenarioId",
            async lazy() {
              const { ScenarioPlanningDetailPage } =
                await import("@redotech/redo-merchant-inventory-app/scenario-planning/scenario-planning-detail");
              return {
                element: (
                  <RedoEmployeeAccessGuard>
                    <ScenarioPlanningDetailPage />
                  </RedoEmployeeAccessGuard>
                ),
              };
            },
          },
        ],
      },
      {
        path: "suppliers",
        handle: {
          breadcrumb: "Suppliers",
          permission: ImsPermissions.ACCESS_SUPPLIERS,
        },
        children: [
          {
            index: true,
            async lazy() {
              const { SuppliersPage } =
                await import("@redotech/redo-merchant-inventory-app/suppliers/suppliers");
              return { Component: SuppliersPage };
            },
          },
          {
            path: ":supplierId",
            handle: { breadcrumb: supplierDetailsBreadcrumb },
            async lazy() {
              const { SupplierDetailsPage } =
                await import("@redotech/redo-merchant-inventory-app/suppliers/supplier-detail");
              const { CatalogProductModalHost } =
                await import("@redotech/redo-merchant-catalog-app/catalog/catalog-product-modal");
              return {
                element: (
                  <>
                    <SupplierDetailsPage
                      breadcrumb={supplierDetailsBreadcrumb}
                    />
                    <CatalogProductModalHost />
                  </>
                ),
              };
            },
          },
        ],
      },
      {
        path: "document-templates",
        handle: {
          breadcrumb: "Document templates",
          permission: ImsPermissions.ACCESS_PURCHASE_ORDERS,
        },
        children: [
          {
            index: true,
            async lazy() {
              const { PoTemplateList } =
                await import("@redotech/redo-merchant-inventory-app/po-templates/po-template-list");
              return { Component: PoTemplateList };
            },
          },
          {
            path: ":templateId",
            handle: { breadcrumb: "Edit template" },
            async lazy() {
              const { PoTemplateBuilder } =
                await import("@redotech/redo-merchant-inventory-app/po-templates/po-template-builder");
              return { Component: PoTemplateBuilder };
            },
          },
        ],
      },
      {
        path: "barcode-label-templates",
        handle: {
          breadcrumb: "Barcode label templates",
          permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
        },
        children: [
          {
            index: true,
            async lazy() {
              const { BarcodeLabelTemplateList } =
                await import("@redotech/redo-merchant-inventory-app/barcode-label-builder/barcode-label-template-list");
              return { Component: BarcodeLabelTemplateList };
            },
          },
          {
            path: ":templateId",
            handle: { breadcrumb: "Edit template" },
            async lazy() {
              const { BarcodeLabelTemplateBuilderPage } =
                await import("@redotech/redo-merchant-inventory-app/barcode-label-builder/barcode-label-template-builder-page");
              return { Component: BarcodeLabelTemplateBuilderPage };
            },
          },
        ],
      },
      {
        path: "transfers",
        handle: {
          breadcrumb: "Transfers",
          permission: ImsPermissions.ACCESS_TRANSFERS,
        },
        children: [
          {
            index: true,
            async lazy() {
              const { TransfersListPage } =
                await import("@redotech/redo-merchant-inventory-app/transfers/transfers-list");
              return { Component: TransfersListPage };
            },
          },
        ],
      },
      {
        path: "purchase-orders",
        handle: {
          breadcrumb: "Purchase orders",
          permission: ImsPermissions.ACCESS_PURCHASE_ORDERS,
        },
        children: [
          defaultChildRoute("all"),
          {
            path: ":viewName",
            handle: { breadcrumb: purchaseOrderViewBreadcrumb },
            async lazy() {
              const { PurchaseOrdersWrapper } =
                await import("@redotech/redo-merchant-inventory-app/purchase-orders/purchase-orders");
              return {
                element: (
                  <PurchaseOrdersWrapper
                    breadcrumb={purchaseOrderViewBreadcrumb}
                  />
                ),
              };
            },
            children: [
              {
                index: true,
                async lazy() {
                  const { PurchaseOrdersPage } =
                    await import("@redotech/redo-merchant-inventory-app/purchase-orders/purchase-orders");
                  return { Component: PurchaseOrdersPage };
                },
              },
              {
                path: ":purchaseOrderId",
                handle: { breadcrumb: purchaseOrderBreadcrumb },
                async lazy() {
                  const { PurchaseOrderPage } =
                    await import("@redotech/redo-merchant-inventory-app/purchase-orders/purchase-order-detail");
                  const { CatalogProductModalHost } =
                    await import("@redotech/redo-merchant-catalog-app/catalog/catalog-product-modal");
                  return {
                    element: (
                      <>
                        <PurchaseOrderPage
                          breadcrumb={purchaseOrderBreadcrumb}
                        />
                        <CatalogProductModalHost />
                      </>
                    ),
                  };
                },
              },
            ],
          },
        ],
      },
      {
        path: "shipments",
        handle: {
          breadcrumb: "Shipments",
          permission: ImsPermissions.ACCESS_SHIPMENTS,
        },
        children: [
          defaultChildRoute("all"),
          {
            path: "damaged",
            handle: { breadcrumb: "Damaged" },
            async lazy() {
              const { DisputesPage } =
                await import("@redotech/redo-merchant-inventory-app/disputes/disputes");
              return { Component: DisputesPage };
            },
          },
          {
            path: ":viewName",
            handle: { breadcrumb: shipmentViewBreadcrumb },
            async lazy() {
              const { ShipmentsWrapper } =
                await import("@redotech/redo-merchant-inventory-app/shipments/shipments");
              return {
                element: (
                  <ShipmentsWrapper breadcrumb={shipmentViewBreadcrumb} />
                ),
              };
            },
            children: [
              {
                index: true,
                async lazy() {
                  const { ShipmentsPage } =
                    await import("@redotech/redo-merchant-inventory-app/shipments/shipments");
                  return { Component: ShipmentsPage };
                },
              },
              {
                path: ":shipmentId",
                handle: { breadcrumb: shipmentDetailBreadcrumb },
                async lazy() {
                  const { ShipmentDetailPage } =
                    await import("@redotech/redo-merchant-inventory-app/shipments/shipment-detail");
                  const { CatalogProductModalHost } =
                    await import("@redotech/redo-merchant-catalog-app/catalog/catalog-product-modal");
                  return {
                    element: (
                      <>
                        <ShipmentDetailPage
                          breadcrumb={shipmentDetailBreadcrumb}
                        />
                        <CatalogProductModalHost />
                      </>
                    ),
                  };
                },
              },
            ],
          },
        ],
      },
      {
        path: "liquidation/dashboard",
        handle: { breadcrumb: "Home" },
        async lazy() {
          const { LiquidationPage } =
            await import("@redotech/redo-merchant-inventory-app/liquidation/liquidation-page");
          return {
            Component: () => (
              <MarketplaceAccessGuard>
                <LiquidationPage />
              </MarketplaceAccessGuard>
            ),
          };
        },
      },
      {
        path: "liquidation/products",
        handle: { breadcrumb: "All listings" },
        async lazy() {
          const { MarketplaceListings } =
            await import("@redotech/redo-merchant-inventory-app/marketplace/marketplace-listings");
          return {
            Component: () => (
              <MarketplaceAccessGuard>
                <MarketplaceListings />
              </MarketplaceAccessGuard>
            ),
          };
        },
      },
      // temporary route for printing barcodes for ASRV excess inventory
      {
        path: "print-barcode",
        handle: { breadcrumb: "Print barcode" },
        async lazy() {
          const { PrintBarcodePage } =
            await import("@redotech/redo-merchant-inventory-app/print-barcode/print-barcode-page");
          return {
            Component: () => (
              <RedoOutletAccessGuard>
                <PrintBarcodePage />
              </RedoOutletAccessGuard>
            ),
          };
        },
      },
    ],
  },
];

export const MarketplaceAccessGuard = memo(function MarketplaceAccessGuard({
  children,
}: {
  children: ReactNode;
}) {
  const team = useRequiredContext(TeamContext);
  const routes = useRequiredContext(MerchantRoutesContext);

  const isMarketplaceEnabled = team.settings.recommerce?.marketplace ?? false;

  if (!isMarketplaceEnabled) {
    // Redirect to main inventory page if marketplace is not enabled
    return <Navigate replace to={routes.inventoryManagementBaseUrl} />;
  }

  return <>{children}</>;
});

export const RedoOutletAccessGuard = memo(function RedoOutletAccessGuard({
  children,
}: {
  children: ReactNode;
}) {
  const team = useRequiredContext(TeamContext);
  const routes = useRequiredContext(MerchantRoutesContext);

  // only available to the Redo Outlet team
  if (team._id !== "68800d96229f8fc10f4dc1ce") {
    return <Navigate replace to={routes.inventoryManagementBaseUrl} />;
  }

  return <>{children}</>;
});

export const ReFulfillmentAccessGuard = memo(function ReFulfillmentAccessGuard({
  children,
}: {
  children: ReactNode;
}) {
  const team = useRequiredContext(TeamContext);
  const routes = useRequiredContext(MerchantRoutesContext);

  if (!teamHasRefulfillmentEnabled(team)) {
    return <Navigate replace to={routes.inventoryManagementBaseUrl} />;
  }

  return <>{children}</>;
});

export const RedoEmployeeAccessGuard = memo(function RedoEmployeeAccessGuard({
  children,
}: {
  children: ReactNode;
}) {
  const routes = useRequiredContext(MerchantRoutesContext);
  const user = useContext(UserContext);

  const isRedoEmployee = isRedoEmailAddress(user?.email);

  if (!isRedoEmployee) {
    return <Navigate replace to={routes.inventoryManagementBaseUrl} />;
  }

  return <>{children}</>;
});
